/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Component, OnInit, ViewChild, OnDestroy} from "@angular/core";
import {ObjectInstance} from "../../models/object-instance";
import {LocationService} from "../../services/location.service";
import {LocationInstance} from "../../models/location-instance";
import {ObjectService} from "../../services/object.service";
import {HistoryService } from "../../services/history.service";
import {MapComponent} from "../map/map.component";
import {HistoryData, HistoryEvent, HistoryDataElementUI, HistoryDataSecondUI, HistoryDataGUI } from "../../models/history-data";
import {Group} from "../../models/group";
import {HistoryDialogComponent} from "./history-dialog.component";
import {EVENT_NAME_AssetExitedLocation, EVENT_NAME_PersonExitedLocation} from "../../app-constants";
import {OwlDateTimeComponent} from "ng-pick-datetime";
import {Point} from "../../models/point";
import { ImageService } from "app/services/image.service";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "../simple-success-error-dialog-box/simple-success-error-dialog-box.component";

declare var $: any;

@Component({
  selector: 'app-history',
  templateUrl: './history.component.html',
  styleUrls: ['./history.component.css'],
})
export class HistoryComponent implements OnInit, OnDestroy {
  @ViewChild('historyDialog') historyDialogComponent: HistoryDialogComponent;
  @ViewChild('historyErrorDialog') historyErrorDialogComponent: HistoryDialogComponent;
  @ViewChild('dtPicker1') dateTimePicker1: OwlDateTimeComponent<string>;
  @ViewChild('dtPicker2') dateTimePicker2: OwlDateTimeComponent<string>;
  /** Reference to the SimpleSuccessErrorDialog used to display error */
@ViewChild('successErrorDialog') 
simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  _dateFrom: Date;
  _dateTo: Date;
  dateFromForAPI: string;
  dateToForAPI: string;
  dateFromAsMilliseconds: number;
  dateToAsMilliseconds: number;
  maximumDate: Date;

  selectedMoments: Date[];
  // Text fields used to search locations/people/assets/vehicles
  locationSearch = "";
  peopleSearch = "";
  assetSearch = "";
  vehicleSearch = "";

  // Arrays of data for locations/people/assets
  locations: LocationInstanceUI[] = [];
  people: PersonUI[] = [];
  assets: ObjectInstanceUI[] = [];

  displayPerimeters = false;
  showBreadcrumbs = false;
  displayZones = true;

  historyEvents: HistoryEvent[];
  startingPositions: HistoryEvent[];
  historyData: HistoryDataSecondUI[] = [];
  historyRangeMin = 0;
  historyRangeMax = 100;
  historyRangeValue = 0;
  historySecondsElapsedAsTime: string;
  historySecondsTotalAsTime: string;
  historySpeed = 1000;
  historyBarDisabled = true;
  historyItemCurrent: HistoryEvent[] = null;
  historyItemNext: HistoryEvent = null;
  currentStepData = -1;
  //assetsMap: Map<string, ObjectInstance>;
  peopleArray: ObjectInstance[];
  cancelHistory = false;
  okButtonEnabled = false;

  // Checkbox above locations, people, assets to select/deselect all
  allLocationsSelected = false;
  allPeopleSelected = false;
  allAssetsSelected = false;

  // Not used yet, but it's on the GUI. Here as a placeholder
  vehicles = [];

  currentLocationInstance: LocationInstance = null;
  selectedLocationInstances: LocationInstance[] = [];

  @ViewChild('historyMap')
  historyMap: MapComponent;

  historyCurrentTime: Date = null;
  historyNumberSeconds = 0;
  historyProgressCheckNumberSeconds = 0;
  historySecondsElapsed = 0;
  historyPaused = true;

  disableControls = false;
  disableRunHistory = true;

  historyDialogStatusText = "";
  historyDialogPercentageDone = 0;
  historyRequestError = "";

  static historySecondsToTimeFormat(historySeconds: number): string {
    const seconds = historySeconds % 60;
    const secondsPadding = (seconds < 10) ? "0" : "";
    const minutes = Math.floor(historySeconds / 60);

    return minutes + ":" + secondsPadding + seconds;
  }

  // Getters and Setters for dateFrom
  get dateFrom(): Date {
    return this._dateFrom;
  }

  set dateFrom(dateFrom: Date) {
    dateFrom.setSeconds(0);
    console.log("DateFrom set as: " + dateFrom);
    this._dateFrom = dateFrom;
    if (this._dateFrom != null) {
      this.dateFromForAPI = this.dateToStringForAPI(dateFrom);
      this.dateFromAsMilliseconds = dateFrom.valueOf();

      this.checkRunParameters();
    }
  }

  // Getters and Setters for dateTo
  get dateTo(): Date {
    return this._dateTo;
  }

  set dateTo(dateTo: Date) {
    dateTo.setSeconds(0);
    console.log("DateTo set as: " + dateTo);
    this._dateTo = dateTo;
    if (this._dateTo != null) {
      this.dateToForAPI = this.dateToStringForAPI(dateTo);
      this.dateToAsMilliseconds = dateTo.valueOf();

      this.checkRunParameters();
    }
  }

  dateToStringForAPI(inputDate: Date): string {
    return inputDate.getFullYear() + "-" + ((inputDate.getMonth() > 8) ? "" : "0") + (inputDate.getMonth() + 1) + "-" +
      ((inputDate.getDate() > 9) ? "" : "0") + inputDate.getDate() + "T" +
      ((inputDate.getHours() > 9) ? "" : "0") +
      inputDate.getHours() + ":" + ((inputDate.getMinutes() > 9) ? "" : "0") + inputDate.getMinutes();
  }

  datesSelected(): void {
    console.warn("Dates selected");
    const dateFromTo = this.selectedMoments;
    if (dateFromTo != null && dateFromTo.length == 2) {
      this.dateFrom = new Date(dateFromTo[0]);
      this.dateTo = new Date(dateFromTo[1]);
    } else {
      // Invalid dates!
      console.warn("Invalid dates selected in History Date/Time picker");
    }
  }

  constructor(
    public locationService: LocationService, 
    public objectService: ObjectService,
    public historyService: HistoryService, 
    private mImageService: ImageService
  ) {
    this.getAllLocations();
    this.getAllAssets();
    this.getAllPeople();
    this.getAllVehicles();
  }

  checkRunParameters(): void {
    this.disableRunHistory = true;

    console.log("Checking run parameters...");
    if (this.selectedMoments[0] && this.selectedMoments[1]) {
      const assetsSelected = this.assets.find(entity => entity.selected == true);
      const peopleSelected = this.people.find(entity => entity.selected == true);
      const locationsSelected = this.locations.find(entity => entity.selected == true);
      if (assetsSelected || peopleSelected || locationsSelected) {
        this.disableRunHistory = false;
      }
    }
  }

  ngOnInit(): void {
    // From 10 minutes ago to now
    const fromDate = new Date();
    fromDate.setMinutes(fromDate.getMinutes() - 10);
    this.selectedMoments = [ fromDate, new Date() ];
    this.maximumDate = new Date();
  }

  ngOnDestroy(): void {
    // Ensure that if the history is running, stop it
    this.cancelHistory = true;
  }

  getAllLocations(): void {
    this.locationService.getAllLocationInstances().then(
      response => {
        if (response != null) {
          for (const locationInstance of response) {
            const locationInstanceUI = new LocationInstanceUI();
            locationInstanceUI.locationInstance = locationInstance;
            locationInstanceUI.selected = false;
            this.locations.push(locationInstanceUI);
          }
        }
      },
      error => {
        this.handleError(error);
      }
    )
  }

  getAllPeople(): void {
    this.objectService.getAllPeopleObjectInstances().then(
      response => {
        if (response != null) {
          for (const user of response) {
            const peopleUI = new PersonUI();
            peopleUI.person = user;
            peopleUI.selected = false;
            this.people.push(peopleUI);
          }
        }
      },
      error => {
        this.handleError(error);
      }
    )
  }

  getAllAssets(): void {
    this.objectService.getAllAssetObjectInstances().then(
      response => {
        if (response != null) {
          for (const objectInstance of response) {
            const objectInstanceUI = new ObjectInstanceUI();
            objectInstanceUI.objectInstance = objectInstance;
            objectInstanceUI.selected = false;
            this.assets.push(objectInstanceUI);
          }
        }
      },
      error => {
        this.handleError(error);
      }
    )
  }

  // Not used yet. Here as a placeholder
  getAllVehicles(): void {

  }

  runHistory(): void {
    if (this.selectedMoments[0] == null || this.selectedMoments[1] == null) {
      this.showHistoryError("History dates INVALID");
      return;
    }

    this.disableControls = true;
    this.cancelHistory = false;
    this.historyDialogStatusText = "Retrieving history from server....";
    this.historyDialogPercentageDone = 0;
    this.historyDialogComponent.show();
    this.datesSelected();

    const self = this;
    setTimeout(function():void { self.runHistoryContinue(self) }, 1000);
  }

  private async runHistoryContinue(self: HistoryComponent): Promise<void> {
    // Gather up all the selected locations, assets and people
    const locationsArray: LocationInstance[] = [];
    for (const locationUI of self.locations) {
      if (locationUI.selected) {
        locationsArray.push(locationUI.locationInstance);
      }
    }

    const assetsArray: ObjectInstance[] = [];
    for (const assetUI of self.assets) {
      if (assetUI.selected) {
        assetsArray.push(assetUI.objectInstance);
      }
    }

    const peopleArray: ObjectInstance[] = [];
    for (const peopleUI of self.people) {
      if (peopleUI.selected) {
        peopleArray.push(peopleUI.person);
      }
    }

    // ************************************************************************
    // Log the data sent to the history service for debugging purposes
    // ************************************************************************
    let locationsString = "";
    for (const location of locationsArray) {
      locationsString += location.name + " ";
    }

    let assetsString = "";
    for (const asset of assetsArray) {
      assetsString += asset.name + " ";
    }

    let peopleString = "";
    for (const people of peopleArray) {
      peopleString += "(" + people.name + ") ";
    }

    console.log("Calling historyService with DateFrom: " + self.dateFromForAPI + ", DateTo: " + self.dateToForAPI + " with locations [" + locationsString +
      "], assets: [" + assetsString + "], people: [" + peopleString + "]");

    // ************************************************************************
    // Call the history function
    // ************************************************************************

    let historyData: HistoryData = null;
    try {
      historyData = await self.historyService.getHistoryMovement(self.dateFromForAPI, self.dateToForAPI, locationsArray, assetsArray, peopleArray);
    } catch (error) {
      let errorMessage: string = null;
      if (error != null) {
        const response: Response = error;
        errorMessage = await response.text();
      }

      self.historyDialogStatusText = "Problem obtaining History Data.  Error: " + (errorMessage != null && errorMessage.length > 0? errorMessage : error);
      self.okButtonEnabled = true;
      self.historyDialogPercentageDone = 100;
      self.resetHistory();
      return null;
    }

    self.historyParse(historyData);
  }

  // Resets all the data back to empty
  resetHistory(): void {
    console.log("ResetHistory()");
    // Remove the dates
    //this.dateFrom = null;
    //this.dateTo = null;

    // Deselect all locations, assets and people
    this.allLocationsSelected = false;
    this.selectOrDeselectAllLocations();

    this.allAssetsSelected = false;
    this.selectOrDeselectAllAssets();

    this.allPeopleSelected = false;
    this.selectOrDeselectAllPeople();

    // Reset all searches
    this.locationSearch = "";
    this.assetSearch = "";
    this.peopleSearch = "";
    this.vehicleSearch = "";

    this.stopHistory();

    this.disableRunHistory = true;

  }

  // Removes history, but keeps all the inputted data
  stopHistory(): void {
    console.log("stopHistory()");
    // Remove all data
    this.historyData = [];

    // re-enable all controls
    this.disableControls = false;

    // remove the map from the screen
    this.currentLocationInstance = null;
    this.selectedLocationInstances = [];
    this.historyMap.removeAllPeopleAndAssets();

    // Remove all the history
    this.historyData = [];
    this.historyPaused = true;
    this.historyBarDisabled = true;

    // Set the history scrub bar back to it's initial defaults
    this.historySecondsElapsedAsTime = "";
    this.historySecondsTotalAsTime = "";
    this.historyCurrentTime = null;
    this.historyRangeValue = 0;
  }

  historyParse(historyDataDatabase: HistoryData): void {
    this.historyEvents = historyDataDatabase.events == null? [] : historyDataDatabase.events;
    this.startingPositions = historyDataDatabase.starting_positions == null? [] : historyDataDatabase.starting_positions;

    this.historyDialogStatusText = "Parsing history....";
    this.historyDialogPercentageDone = 20;

    for (const historyDatum of this.historyEvents) {
      if (historyDatum.timestamp < this.dateFromAsMilliseconds) {
        historyDatum.timestamp = this.dateFromAsMilliseconds;
      } else {
        break;
      }
    }

    // Calculate how many seconds in length this historyData is
    this.historyNumberSeconds = Math.floor((this.dateTo.getTime() - this.dateFrom.getTime()) / 1000);
    this.historySecondsTotalAsTime = HistoryComponent.historySecondsToTimeFormat(this.historyNumberSeconds);
    this.historySecondsElapsed = 0;
    this.historySecondsElapsedAsTime = HistoryComponent.historySecondsToTimeFormat(this.historySecondsElapsed);

    this.historyCurrentTime = this.dateFrom;

    // create the history data that we will be using

    // Find all the assets and people that we selected
    /*this.assetsMap = new Map();
    for (const assetUI of this.assets) {
      if (assetUI.selected) {
        const historyDataElementUI = new HistoryDataElementUI();
        historyDataElementUI.historyData = new HistoryDataGUI();
        historyDataElementUI.historyData.entityId = assetUI.objectInstance.object.id;
        historyDataElementUI.historyData.entityType = "ASSET";
        historyDataElementUI.historyData.point = null;
        historyDataElementUI.historyDataLastChanged = -1;
        this.assetsMap[assetUI.objectInstance.object.id] = historyDataElementUI;
      }
    }

    this.peopleArray = [];
    for (const peopleUI of this.people) {
      if (peopleUI.selected) {
        this.peopleArray.push(peopleUI.person);
      }
    }

  */

    // Do starting positions
    if (this.startingPositions.length > 0) {
      const historySecond = new HistoryDataSecondUI();
      historySecond.historyDataElement = [];
      for (const item of this.startingPositions) {
        console.log("Adding startingPosition for id: " + item.instance_id);
        const historyDataElement = new HistoryDataElementUI();
        const historyData = new HistoryDataGUI();
        historyData.entityId = item.instance_id;
        historyData.entityType = item.entity_type.toUpperCase();
        historyData.locationId = item.location_id;
        historyData.point = item.position;
        historyData.timestamp = this.dateFromAsMilliseconds;
        historyDataElement.historyData = historyData;
        historySecond.historyDataElement.push(historyDataElement);
      }
      this.historyData.push(historySecond);
    } else {
      console.log("No starting positions found");
    }
    this.historyItemCurrent = null;
    this.historyItemNext = null;
    this.currentStepData = 0;

    this.historyDialogStatusText = "Indexing history....";
    this.historyDialogPercentageDone = 30;
    this.historyProgressCheckNumberSeconds = Math.floor(this.historyNumberSeconds / 70);
    // Loop through every second of what we're showing on the GUI
    console.log("historyNumberSeconds = " + this.historyNumberSeconds);

    if (this.historyEvents.length == 0 && this.startingPositions.length == 0) {
      this.historyDialogStatusText = "ERROR: There is no data for this history request";
      this.historyDialogPercentageDone = 100;
      this.okButtonEnabled = true;
    } else {
      const self = this;
      setTimeout(function (): void {
        self.historyParseNextStep(self, 0)
      }, 0);
    }
  }

  changeCurrentMap(index: number){
    this.currentLocationInstance = this.selectedLocationInstances[index];
  }

  historyParseNextStep(self: HistoryComponent, historySecond: number): void {
    if (self.cancelHistory) {
      console.log("History indexing cancelled");
      this.resetHistory();
      return;
    }

    self.historyDialogPercentageDone = Math.floor(30 + ((historySecond / self.historyNumberSeconds) * 70));

    const millisecondsNow = self.dateFromAsMilliseconds + (historySecond * 1000);
    // Create a history second which details what historyData each element has every second

    // Create a UI history second
    const historySecondUI = new HistoryDataSecondUI();
    historySecondUI.historyDataElement = [];

    // do/while loop to add events to the historySecond for each additional event that occurs in the same second
    do {

      if (self.currentStepData == 0) {
        // *****************
        // FIRST STEP
        // *****************
        if (self.historyData.length == 1) {
          self.historyItemCurrent = self.startingPositions;
        } else {
          self.historyItemCurrent = null;
        }

        if (self.historyEvents.length == 0) {
          self.historyItemNext = null;
        } else {
          self.historyItemNext = self.historyEvents[0];
        }

        // Set the step for the second iteration to 1
        self.currentStepData = 1;
      } else {
        // *********************
        // SECOND STEP ONWARDS
        // *********************
         if (self.historyItemNext && (self.historyItemNext.timestamp <= millisecondsNow)) {
           if (self.currentStepData >= self.historyEvents.length) {
             // If we reached the end of the data, next will be null from now on
             // self.historyItemCurrent remains the same
             self.historyItemNext = null;
           } else {
             const historyItemCurrentNew = [];

             // put the old values into the new currentHistoryItem....
             if (self.historyItemCurrent) {
               for (const historyItem of self.historyItemCurrent) {
                 const historyItemNew = new HistoryEvent();
                 historyItemNew.timestamp = historyItem.timestamp;
                 historyItemNew.instance_id = historyItem.instance_id;
                 if (historyItem.position) {
                   historyItemNew.position = new Point();
                   historyItemNew.position.x = historyItem.position.x;
                   historyItemNew.position.y = historyItem.position.y;
                 }
                 historyItemNew.location_id = historyItem.location_id;
                 historyItemNew.entity_type = historyItem.entity_type;

                 historyItemCurrentNew.push(historyItemNew);
               }
             }

             // update any values that need to be updated
             const currentStepHistoryEvent = self.historyEvents[self.currentStepData];
             let found = false;
             for(const historyItem of historyItemCurrentNew) {
               if (historyItem.instance_id == currentStepHistoryEvent.instance_id) {
                 found = true;

                 if (currentStepHistoryEvent.movement_type == "Movement") {
                   historyItem.location_id = currentStepHistoryEvent.location_id;
                   if (historyItem.location_id == null) {
                     console.log("HISTORY ITEM LOCATION ID IS NULL");
                   }
                   if (historyItem.position) {
                     historyItem.position = new Point();
                     historyItem.position.x = currentStepHistoryEvent.position.x;
                     historyItem.position.y = currentStepHistoryEvent.position.y;
                   }
                 } else if (currentStepHistoryEvent.movement_type == "Entered_Location") {
                   historyItem.position = new Point();
                   historyItem.position.x = currentStepHistoryEvent.position.x;
                   historyItem.position.y = currentStepHistoryEvent.position.y;
                 } else if (currentStepHistoryEvent.movement_type == "Exited_Location") {
                   historyItem.position = new Point();
                   historyItem.position.x = -1000;
                   historyItem.position.y = -1000;
                 }

               }

             }

             if (found == false) {
               console.log("Found a new item!");
               const historyItemNew = new HistoryEvent();
               historyItemNew.timestamp = currentStepHistoryEvent.timestamp;
               historyItemNew.instance_id = currentStepHistoryEvent.instance_id;
               if (currentStepHistoryEvent.position) {
                 historyItemNew.position = new Point();
                 historyItemNew.position.x = currentStepHistoryEvent.position.x;
                 historyItemNew.position.y = currentStepHistoryEvent.position.y;
               }
               historyItemNew.location_id = currentStepHistoryEvent.location_id;
               historyItemNew.entity_type = currentStepHistoryEvent.entity_type;

               historyItemCurrentNew.push(historyItemNew);
             }

             self.historyItemCurrent = historyItemCurrentNew;
             self.historyItemNext = self.historyEvents[self.currentStepData + 1];

             // Increase the step for the next iteration
             self.currentStepData++;
           }
         }
      }

      // Add all these to the dataElement.historyData
      if (self.historyItemCurrent) {
        for (const historyItem of self.historyItemCurrent) {
          const historyDataElementUI = new HistoryDataElementUI();
          historyDataElementUI.historyData = new HistoryDataGUI();
          historyDataElementUI.historyData.entityType = historyItem.entity_type.toUpperCase();
          historyDataElementUI.historyData.entityId = historyItem.instance_id;
          historyDataElementUI.historyData.point = historyItem.position;
          historyDataElementUI.historyData.timestamp = historyItem.timestamp;

          historySecondUI.historyDataElement.push(historyDataElementUI);
        }
      }
    } while (self.historyItemNext != null && self.historyItemNext.timestamp <= millisecondsNow);

    self.historyData.push(historySecondUI);

    historySecond++;
    if (historySecond < self.historyNumberSeconds) {
      // Every 500 pieces of data, update the GUI
      if (historySecond % self.historyProgressCheckNumberSeconds == 0) {

        setTimeout(() => {
          self.historyParseNextStep(self, historySecond)
        }, 0);
      } else {
        try {
          self.historyParseNextStep(self, historySecond);
        } catch(error) {
          self.historyDialogStatusText = "ERROR: History Query results exceed browser memory limits. Please reduce length of query.";
          self.okButtonEnabled = true;
        }
      }
    } else {
      self.historyDialogComponent.hide();

      this.historyMap.turnTrackingOff();

      //add all selected locations into selectedLocationInstances
      for(const locInst of this.locations) {
        if (locInst.selected) {
          this.selectedLocationInstances.push(locInst.locationInstance);
        }
      }

      //add all selected people's locations into selectedLocationInstances
      for (const personInst of this.people) {
        if (personInst.selected) {
          if (!this.locationIsSelected(personInst.person.locationInstance)) {
            this.selectedLocationInstances.push(personInst.person.locationInstance);
          }
        }
      }
      //add all selected asset's locations into selectedLocationInstances
      for (const assetInst of this.assets) {
        if (assetInst.selected) {
          if (!this.locationIsSelected(assetInst.objectInstance.locationInstance)) {
            this.selectedLocationInstances.push(assetInst.objectInstance.locationInstance);
          }
        }
      }
      //no location instance, person, or asset selected. default to the first location available
      if (!this.selectedLocationInstances) {
        console.log(`No location selected. Defaulting to ${this.locations[0].locationInstance.name}`)
        this.selectedLocationInstances.push(this.locations[0].locationInstance);
      }

      //set currently selected location to first instance in the array
      this.currentLocationInstance = this.selectedLocationInstances[0];

      this.historyRangeMin = 0;
      this.historyRangeMax = this.historyData.length;

      $("#playHistory").css("display", "block");
      this.historyBarDisabled = false;

    }
  }

  // if passed location is already in selectedLocationInstances, return true
  locationIsSelected(locationInstance: LocationInstance): boolean{
    for(let i = 0; i < this.selectedLocationInstances.length; i++){
      if(this.selectedLocationInstances[i].id == locationInstance.id){
        return true;
      }
    }
    return false;
  }

  // Plays the 'historySecondsElapsed' step then makes calls to playNextStopOfHistory to play additional steps of history
  playHistory(): void {
    console.log("playHistory");

    // Only play if we haven't reached the end
    if (this.historySecondsElapsed < this.historyNumberSeconds) {
      this.historyBarDisabled = true;
      this.historyPaused = false;

      this.historyMap.turnTrackingOff();

      const historyDatum = this.historyData[this.historySecondsElapsed];
      console.log("Looking at second " + this.historySecondsElapsed + "...");
      this.historyMap.activateTweening(false);
      if (historyDatum.historyDataElement) {
        for (const historyDatumElement of historyDatum.historyDataElement) {
          const elementType = historyDatumElement.historyData.entityType;
          console.log("  Moving " + elementType + " " + historyDatumElement.historyData.entityId + "....");
          this.historyMap.updateWithMovementEvent(elementType,
            historyDatumElement.historyData.entityId, historyDatumElement.historyData.point, this.currentLocationInstance);
        }
      }
      const self = this;
      setTimeout(function(): void { self.playNextStepOfHistory(self) },  this.historySpeed);
    }
  }

  playNextStepOfHistory(self: any): void {
    // allow tweening while the play button is active
    self.historyMap.activateTweening(true);
    self.historyMap.setTweenDelay(this.historySpeed / 2);

    if (self.historyPaused == false) {
      self.historySecondsElapsed++;
      self.historySecondsElapsedAsTime = HistoryComponent.historySecondsToTimeFormat(self.historySecondsElapsed);
      self.historyCurrentTime = new Date((self.historySecondsElapsed * 1000) + self.dateFromAsMilliseconds);

      if (self.historyData.length > self.historySecondsElapsed) {
        //console.log("playing history second " + self.historySecondsElapsed);
        const historyDatum = self.historyData[self.historySecondsElapsed];
        console.log("Looking at second " + this.historySecondsElapsed + "...");

        self.historyRangeValue = self.historySecondsElapsed;

        // Loop through and find each event self is before this new date a second later...
        for(const historyDatumElement of historyDatum.historyDataElement) {
          const elementType = historyDatumElement.historyData.entityType;
          console.log("  Moving " + elementType + " " + historyDatumElement.historyData.entityId + "....");
          self.historyMap.updateWithMovementEvent(elementType,
            historyDatumElement.historyData.entityId, historyDatumElement.historyData.point, self.locationInstance);
        }

        // Queue another event
        setTimeout(function(): void { self.playNextStepOfHistory(self) }, self.historySpeed);
      } else {
        // Ran out of history data... reset to beginning
        this.pauseHistory();
        self.historySecondsElapsed = 0;
        self.historySecondsElapsedAsTime = HistoryComponent.historySecondsToTimeFormat(self.historySecondsElapsed);
        self.historyCurrentTime = new Date((self.historySecondsElapsed * 1000) + self.dateFromAsMilliseconds);

        const historyDatum = self.historyData[self.historySecondsElapsed];
        self.historyRangeValue = self.historySecondsElapsed;

        // Loop through and find each event self is before this new date a second later...
        for(const historyDatumElement of historyDatum.historyDataElement) {
          self.historyMap.updateWithMovementEvent(historyDatumElement.historyData.entityType,
            historyDatumElement.historyData.entityId, historyDatumElement.historyData.point, self.locationInstance);
        }

      }
    }

  }

  pauseHistory(): void {
    this.historyPaused = true;
    this.historyBarDisabled = false;
  }

  cancelHistoryButtonClicked(): void {
    this.cancelHistory = true;
    this.resetHistory();
  }

  selectOrDeselectAllLocations(): void {
    for (const location of this.locations) {
      location.selected = this.allLocationsSelected;
    }
  }

  selectOrDeselectAllPeople(): void {
    for (const person of this.people) {
      person.selected = this.allPeopleSelected;
    }
  }

  selectOrDeselectAllAssets(): void {
    for (const asset of this.assets) {
      asset.selected = this.allAssetsSelected;
    }
  }

  handleError(error: any): void {
    console.error("handleError() - Error: " + error);
    if (error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }
  }

  locationSearchValueChanged(): void {
    // Update all the locations with just the ones matching the name entered (or selected)
    // Note that any previously selected location will stay viewable
    for (const locationUI of this.locations) {
      if (this.locationSearch == "" || locationUI.selected) {
        locationUI.viewable = true;
      } else {
        locationUI.viewable = (locationUI.locationInstance.name.toLowerCase().indexOf(this.locationSearch.toLowerCase()) != -1);
      }
    }
  }

  assetSearchValueChanged(): void {
    // Update all the assets with just the ones matching the name entered (or selected)
    // Note that any previously selected asset will stay viewable
    for (const assetUI of this.assets) {
      if (this.assetSearch == "" || assetUI.selected) {
        assetUI.viewable = true;
      } else {
        assetUI.viewable = (assetUI.objectInstance.name.toLowerCase().indexOf(this.assetSearch.toLowerCase()) != -1);
      }
    }
  }

  peopleSearchValueChanged(): void {
    // Update all the people with just the ones matching the name entered (or selected)
    // Note that any previously selected person will stay viewable
    for (const peopleUI of this.people) {
      if (this.peopleSearch == "" || peopleUI.selected) {
        peopleUI.viewable = true;
      } else {
        const personName = peopleUI.person.name;
        peopleUI.viewable = (personName.toLowerCase().indexOf(this.peopleSearch.toLowerCase()) != -1);

      }
    }
  }

  historyRangeChanged(event: any): void {
    console.log("historyRangeChanged");
    if (this.historySecondsElapsed >= this.historyData.length) {
      // We have reached the end - show the second behind
      this.historySecondsElapsed = this.historyData.length - 1;
    }

    // TODO: Save the previous history range so we can remove all icons that do not appear in new history
    const oldHistoryDatum = this.historyData[this.historySecondsElapsed];
    if (!oldHistoryDatum) {
      //oldHistoryDatum = this.historyData[this.historyData.length - 1];
      console.warn("No oldHistoryDatum!");
    }
    const oldMap = {};
    for(const oldHistoryDatumElement of oldHistoryDatum.historyDataElement) {
      oldMap[oldHistoryDatumElement.historyData.entityId] = true;
    }

    // Update the history parameters to the selected range value
    this.historyRangeValue = event.currentTarget.value;
    this.historySecondsElapsed = this.historyRangeValue;
    this.historySecondsElapsedAsTime = HistoryComponent.historySecondsToTimeFormat(this.historySecondsElapsed);
    this.historyCurrentTime = new Date((this.historySecondsElapsed * 1000) + this.dateFromAsMilliseconds);

    // Loop through and find each event that is before this new date a second later...
    if (this.historySecondsElapsed >= this.historyData.length) {
      this.historySecondsElapsed = this.historyData.length - 1;
    }
    const historyDatum = this.historyData[this.historySecondsElapsed];

    // remove tweening and breadcrumbs when moving to different points in history.
    this.historyMap.activateTweening(false);
    this.historyMap.removeBreadcrumbs();

    if (historyDatum.historyDataElement) {
      for (const historyDatumElement of historyDatum.historyDataElement) {
        this.historyMap.updateWithMovementEvent(historyDatumElement.historyData.entityType,
          historyDatumElement.historyData.entityId, historyDatumElement.historyData.point, this.currentLocationInstance);
        // Note if any of these elements appear in the next history
        oldMap[historyDatumElement.historyData.entityId] = false;
      }
    }

    // Remove any elements that are not shown
    if (oldHistoryDatum.historyDataElement) {
      for (const oldHistoryDatumElement of oldHistoryDatum.historyDataElement) {
        if (oldMap[oldHistoryDatumElement.historyData.entityId] == true) {
          if (oldHistoryDatumElement.historyData.entityType && oldHistoryDatumElement.historyData.entityType == "ASSET") {
            // Remove this element
            console.log("historyRangeChanged: removing asset");

            this.historyMap.updateWithAssetEvent(oldHistoryDatumElement.historyData.entityId, EVENT_NAME_AssetExitedLocation, null);
          } else {
            console.log("historyRangeChanged: removing user");
            this.historyMap.updateWithPersonEvent(oldHistoryDatumElement.historyData.entityId, EVENT_NAME_PersonExitedLocation, null);
          }
        }
      }
    }

  }

  setHistorySpeed(speed: number): void {
    this.historySpeed = speed;
  }

  selectPersonGroup(group: Group, selected: boolean): void {
    // highlight or unhighlight this group for the viewable users on the screen
    for(const person of this.people) {
      if (person.viewable) {
        for(const userGroup of person.person.groups) {
          if (userGroup.id == group.id) {
            person.selected = !selected;
          }
        }
      }
    }
  }

  selectAssetGroup(group: Group, selected: boolean): void {
    // highlight or unhighlight this group for the viewable assets on the screen
    for(const asset of this.assets) {
      if (asset.viewable) {
        for(const assetGroup of asset.objectInstance.groups) {
          if (assetGroup.id == group.id) {
            asset.selected = !selected;
          }
        }
      }
    }
  }

  showHistoryError(errorMessage: string): void {
    console.warn(errorMessage);
    this.historyRequestError = errorMessage;
    this.historyErrorDialogComponent.show();
  }

  showInitialPositions(): void {
    // Show initial positions of all assets/people
    console.log("Displaying initial positions of history components at second 0");
    const historyDatum = this.historyData[0];
    this.historyMap.activateTweening(false);
    if (historyDatum.historyDataElement) {
      for (const historyDatumElement of historyDatum.historyDataElement) {
        console.log("Placing " + historyDatumElement.historyData.entityType + " " + historyDatumElement.historyData.entityId + " at starting position.");
        this.historyMap.updateWithMovementEvent(historyDatumElement.historyData.entityType,
          historyDatumElement.historyData.entityId, historyDatumElement.historyData.point, this.currentLocationInstance);
      }
    }
  }

  public getIconSourceForObjectInstance(objectInstance: ObjectInstance): string {
    return this.mImageService.getIconSourceForObjectInstance(objectInstance);
  }

}

class LocationInstanceUI {
  viewable = true;
  selected = false;
  locationInstance: LocationInstance;
}

class PersonUI {
  viewable = true;
  selected = false;
  person: ObjectInstance;
}

class ObjectInstanceUI {
  viewable = true;
  selected = false;
  objectInstance: ObjectInstance;
}


